"use client";
import Icon from "@/components/icons/Icons";
import Image from "next/image";
import { apiPost } from "@/utils/apiClient";
import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import CustomModal from "@/components/modal/CustomModal";
import { useLoginModal } from "@/utils/useLoginModal";
import { SIFSecondaryButton } from "../ui/SIFSecondaryButton";
import { SIFPrimaryButton } from "../ui/SIFPrimaryButton";
import { SIFStrategyApiResponse, SIFStrategyQuizQuestion } from "@/type/sifPageTypes";
import {
  getIsinFromAnswers,
  parseFundSIFSuitability,
  riskCategoryDescriptions,
  transformQuizData,
} from "@/utils/sifPageFunctions";
import { SchemesByUCode } from "@/type/commonTypes";
import { formatCompactNumber, normalizeRiskCategory } from "@/utils/commonFunctions";
import { usePathname, useRouter } from "next/navigation";
import { addToast } from "@/components/ui/CustomToastManager";
import Link from "next/link";
import ExpandableSubheading from "../others/ExpandableSubheading";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { closeSIFStrategyModal } from "@/redux/features/SIFStrategyModal/sifStrategySlice";
import { DASHBOARD_ROOT, ROOT_URL } from "@/utils/gloableVariables";

type ScreenType = "quiz" | "result";

type AnswerItem = {
  question_id: number;
  option_id: number;
  answer: string;
};

const subject_group = "SIF Requirement";

export default function SIFStrategyModal() {  

  const dispatch = useDispatch();
  const isOpen = useSelector((state: RootState) => state.sifStrategyModal.isOpen);

  const { isLoggedIn, open, setModalKey } = useLoginModal();
  const [quizData, setQuizData] = useState<SIFStrategyQuizQuestion[]>([]);
  const [schemeData, setschemeData] = useState<SchemesByUCode | null>(null);

  const [screen, setScreen] = useState<ScreenType>("quiz");
  const [step, setStep] = useState(0);

  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [loader, setLoader] = useState(false);
  const [apiLoading, setApiLoading] = useState(false);

  const pathName = usePathname();
  const route = useRouter();

  // ================= API Questions Data =================
  useEffect(() => {
    if (!isOpen) return;
    setApiLoading(true);

    const fetchQuestions = async () => {
      try {
        const res = await apiPost("feedback/getQuestion", { subject_group });
        if (!res.status) throw new Error(res.message || "Something went wrong Getting SIF Strategy");

        const apiData = res.result[subject_group] as SIFStrategyApiResponse;
        const quizData = transformQuizData(apiData);

        setQuizData(quizData);
      } catch (error) {
        console.error("Error fetching SIF Strategy quiz questions:", error);
      } finally {
        setApiLoading(false);
      }
    };

    fetchQuestions();
  }, [isOpen]);

  const selectedIsin = useMemo(() => {
    if (step < quizData.length - 1 || quizData.length === 0) return null;
    return getIsinFromAnswers(answers);
  }, [answers, screen]);

  // ================= API Scheme Data =================
  async function handleFetchScheme() {
    if (!selectedIsin) return;

    try {
      const res = await apiPost("api/getSchemesByUCode", { isin: [selectedIsin] }, { base: "none" });
      if (!res.success) throw new Error("Something went wrong Getting SIF Schemes");

      setschemeData(res.data[0]);
      setScreen("result");

      handleAnswerSubmit({ scheme: res.data[0] });
    } catch (error) {
      console.error("Error fetching SIF Strategy schemes:", error);
    }
  }

  useEffect(() => {
    if (!isOpen) {
      setScreen("quiz");
      setStep(0);
      setAnswers({});
    }
  }, [isOpen]);

  const current = quizData[step];

  // ================= NAVIGATION =================
  const handleNext = () => {
    if (!answers[current.step]) return;

    if (step < quizData.length - 1) {
      setStep((p) => p + 1);
    } else {
      if (isLoggedIn) {
        handleFetchScheme();
      } else {
        open();
        setModalKey("sif-strategy-process");
      }
    }
  };

  useEffect(() => {
    const isLastStep = step === quizData.length - 1;

    if (isLastStep && isLoggedIn && Object.keys(answers).length === quizData.length) {
      handleFetchScheme();
    }
  }, [isLoggedIn]);

  const handleBack = () => {
    if (step > 0) setStep((p) => p - 1);
  };

  const normalizedRisk = normalizeRiskCategory(schemeData?.scheme_risk || "medium");
  const schemeSuitabilityData = parseFundSIFSuitability(schemeData?.suitability);

  // ================= ADD TO CART / SINGLE BUY =================
  const handleCartAction = async () => {
    if (!isLoggedIn || !schemeData) return setLoader(false);

    setLoader(true);

    const payload = {
      scheme_code: schemeData.scheme_code || "",
      inv_type: "lumpsum",
      amount: String(schemeData.minimum_investment) ?? "0",
      sip_date: "1",
      offset: "",
      addnl_investment: "",
      folio_no: "",
      source: pathName,
      medium: "Web",
      folio: "",
    };

    try {
      const res = await apiPost("addToCart", payload);

      if (res.status) {
        route.push(`${DASHBOARD_ROOT}single-buy/${res.cart_id}`);
      } else {
        addToast({
          title: "Add To Cart",
          description: "Failed to add to cart!",
          status: "failed",
        });
      }
    } catch (err) {
      console.error(err);
      addToast({
        title: "Add To Cart",
        description: "Something Went Wrong!",
        status: "failed",
      });
    } finally {
      setLoader(false);
    }
  };

  const resultHeading = useMemo(() => {
    if (!quizData.length) return "Preserve capital with steady growth";

    const firstQuestion = quizData[0];
    const selectedValue = answers[firstQuestion?.id];

    if (!selectedValue) return "Preserve capital with steady growth";

    const selectedOption = firstQuestion.options.find((opt) => opt.value === selectedValue);

    return selectedOption?.label ?? "";
  }, [answers, quizData]);

  // ================= Submit Answers =================
  const answerList = useMemo<AnswerItem[]>(() => {
    return quizData
      .map((question) => {
        const selectedValue = answers[question.step];

        if (!selectedValue) return null;

        const selectedOption = question.options.find((opt) => opt.value === selectedValue);
        if (!selectedOption) return null;

        return {
          question_id: question.id,
          option_id: selectedOption.id,
          answer: selectedOption.label,
        };
      })
      .filter((item): item is AnswerItem => item !== null);
  }, [answers, quizData]);

  const handleAnswerSubmit = async ({ scheme }: { scheme: SchemesByUCode }) => {
    if (!scheme || !answerList) {
      return;
    }

    const payload = {
      subject: subject_group,
      answerList,
      device: "Web",
      schemeData: null,
    };

    try {
      const res = await apiPost("feedback/submitFeedback", payload);

      if (!res.status) throw new Error(res.message || "Something went wrong");
    } catch (err) {
      console.error(err);
      addToast({
        title: "Add To Cart",
        description: "Something Went Wrong!",
        status: "failed",
      });
    }
  };

  if (quizData.length === 0 || !isOpen) return null;

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={() => dispatch(closeSIFStrategyModal())}
      maxSize="max-w-130"
      className="bg-[#000A1A]! rounded-t-8xl! sm:rounded-6xl! border border-b-0 sm:border-b border-white/30 sm:border-white overflow-hidden"
      showCloseButton={false}
      closeOnOverlayClick={false}
      isBlurOverlay
    >
      <AnimatePresence mode="wait">
        {/* ================= QUIZ SCREEN ================= */}
        {screen === "quiz" && (
          <motion.div
            key="quiz"
            initial={{ x: 60, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -60, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="flex flex-col p-5 sm:p-8 h-120 text-white"
          >
            {apiLoading ? (
              <div className="text-lg sm:text-xl font-medium text-center my-auto">Loading Data, Please Wait...</div>
            ) : (
              <>
                {/* HEADER */}
                <div className="mb-6">
                  <div className="flex justify-between items-center mb-3.5">
                    <p className="text-xl sm:text-2xl font-bold">Question {step + 1} of 3</p>
                    <button
                      onClick={() => dispatch(closeSIFStrategyModal())}
                      aria-label="Popup Close Icon"
                      className="group p-2"
                    >
                      <Icon.crossIcon className="w-4 h-4 opacity-70 transition group-hover:opacity-100" />
                    </button>
                  </div>

                  <div className="h-1 bg-white/10 rounded-sm">
                    <div className="h-full bg-[#2AB625]" style={{ width: `${((step + 1) / 3) * 100}%` }} />
                  </div>
                </div>

                {/* BODY (inner animation) */}
                <div className="flex-1 mb-6">
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={step}
                      initial={{ x: 40, opacity: 0 }}
                      animate={{ x: 0, opacity: 1 }}
                      exit={{ x: -40, opacity: 0 }}
                    >
                      <div className="text-lg sm:text-xl font-medium sm:font-bold mb-4 sm:mb-6">{current.question}</div>

                      <div className="space-y-3">
                        {current.options.map((opt) => {
                          const active = answers[current.step] === opt.value;

                          return (
                            <button
                              key={opt.id}
                              onClick={() =>
                                setAnswers((p) => ({
                                  ...p,
                                  [current.step]: opt.value,
                                }))
                              }
                              className={`w-full text-sm text-left px-3.5 py-3 xs:px-4 xs:py-4.5 rounded-xl sm:rounded-2xl border transition ${active ? "bg-white/15 border-white/95 text-white" : "border-white/15 text-[#D1D5DC] hover:border-white/50 hover:bg-white/5"}`}
                            >
                              {opt.label}
                            </button>
                          );
                        })}
                      </div>
                    </motion.div>
                  </AnimatePresence>
                </div>

                {/* FOOTER */}
                <div className="flex justify-between gap-4 sm:gap-6 pt-5 sm:p-0 border-t border-white/20 sm:border-0">
                  <SIFSecondaryButton
                    onClick={handleBack}
                    secondaryBg={false}
                    display="flex"
                    disabled={step === 0}
                    className="py-2.5!"
                  >
                    <Icon.rightArrow className="rotate-180" />
                    Go Back
                  </SIFSecondaryButton>

                  <SIFPrimaryButton
                    onClick={handleNext}
                    display="flex"
                    disabled={!answers[current.step]}
                    className="py-2.5!"
                  >
                    {step === 2 ? "Result" : "Next"}
                    <Icon.rightArrow />
                  </SIFPrimaryButton>
                </div>
              </>
            )}
          </motion.div>
        )}

        {/* ================= RESULT ================= */}
        {screen === "result" && (
          <motion.div
            key="result"
            initial={{ x: 60, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            className="relative sm:h-120 text-white text-center p-4 sm:p-5.5 overflow-y-auto scrollbar-xs"
          >
            <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(111.8%_111.8%_at_50%_0%,#3b82f642_0%,rgba(0,0,0,0)_70%)]" />

            <div className="relative flex justify-center mb-2">
              <span className="inline-block py-1.5 px-5.5 rounded-full border border-[#E4D7FF33] bg-[#4E05DF1A] text-[#B995FF] text-xs font-bold uppercase tracking-[0.6px]">
                Recommended Strategy
              </span>

              <button
                onClick={() => dispatch(closeSIFStrategyModal())}
                aria-label="Popup Close Icon"
                className="absolute inset-y-0 right-0 my-auto group p-2"
              >
                <Icon.crossIcon className="w-4 h-4 opacity-70 transition group-hover:opacity-100" />
              </button>
            </div>

            <div className="text-xl sm:text-2xl font-bold mb-1.5">{resultHeading}</div>

            <ExpandableSubheading subHeading={schemeSuitabilityData?.type} />

            <div className="border border-white/50 bg-[linear-gradient(180deg,rgba(255,255,255,0.14)_0%,rgba(255,255,255,0.052)_100%)] rounded-4xl p-4 text-left mb-6 space-y-3">
              <div className="flex items-center gap-3 group" title={schemeData?.scheme_name ?? ""}>
                <Image
                  src={`${ROOT_URL}img/AMCsLogo/${schemeData?.amc_code}.png`}
                  alt={schemeData?.scheme_name ?? "Fund Logo"}
                  width={99}
                  height={99}
                  className="w-10 h-10 shrink-0 object-contain rounded-full"
                />

                <div className="text-white">
                  <div className="font-bold sm:text-lg leading-1.2! line-clamp-1">{schemeData?.scheme_name}</div>
                  <span className="inline-block font-medium text-2xs sm:text-sm leading-1.3! opacity-50">
                    {schemeData?.scheme_category ?? "Others"}
                  </span>
                </div>
              </div>

              <p
                className="text-2xs leading-1.3 text-[#99A1AF] line-clamp-2"
                title={riskCategoryDescriptions[normalizedRisk]}
              >
                {riskCategoryDescriptions[normalizedRisk]}
              </p>

              <div className="flex items-center bg-white/20 rounded-2lg p-2.5 sm:p-3">
                <div className="w-full text-white">
                  <p className="opacity-60 text-2xs leading-1.3 mb-1 sm:mb-1.5">Min Inv.</p>
                  <p className="font-medium">
                    ₹ {formatCompactNumber(schemeData?.minimum_investment ?? 0, false, 2, false)}
                  </p>
                </div>

                <div className="w-full text-white">
                  <p className="opacity-60 text-2xs leading-1.3 mb-1 sm:mb-1.5">Returns (3M)</p>
                  <p
                    className={`font-medium ${schemeData?.three_month_abs && schemeData?.three_month_abs > 0 ? "text-green-500" : "text-red-500"}`}
                  >
                    {schemeData?.three_month_abs ?? 0}%
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex flex-wrap-reverse xs:flex-nowrap gap-2.5">
                <Link
                  href="https://wa.me/9660032889"
                  className="block w-full px-8 py-2 sm:py-3 font-bold text-sm sm:text-base border border-white/50 rounded-xl transition hover:bg-white/20 whitespace-nowrap"
                >
                  Talk to our Experts
                </Link>

                <button
                  onClick={handleCartAction}
                  disabled={loader}
                  className="flex w-full items-center justify-center gap-2 px-8 py-2 sm:py-3 font-bold text-sm sm:text-base border border-white/50 rounded-xl bg-white text-black transition hover:bg-transparent hover:text-white whitespace-nowrap"
                >
                  Start Investing
                  {loader && <Icon.buttonLoaderSecondary className="w-4 h-4" />}
                  {!loader && <Icon.rightArrow className="w-4 h-4" />}
                </button>
              </div>
              <p className="flex items-center justify-center gap-1 text-xs text-[#8C8C8C] mt-2">
                <Icon.lockIconV1 className="w-3 h-3" />
                Your data is encrypted & secure. No spam, ever.
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </CustomModal>
  );
}
